import { Link } from "react-router-dom";
import { PUBLIC_ROUTES } from "../../routes";
import MarketingShell from "./MarketingShell";

const SUPPORT = [
  {
    title: "Hands for the things that hurt",
    body: "Reaching the top shelf, lifting the kettle when the wrist is bad, helping out of the bath, fetching the slippers from the bedroom. The small physical jobs that turn into falls when they&rsquo;re forced.",
  },
  {
    title: "Eyes that catch a fall before it lands",
    body: "Arthur reads gait. If Margaret looks unsteady reaching for the cabinet, he&rsquo;s already moved. If she does fall, he&rsquo;s on his knees beside her in under a second, with the family and ambulance already called.",
  },
  {
    title: "Company that lasts the day",
    body: "He remembers what Margaret said yesterday about her sister. He turns on the radio for the four o&rsquo;clock play. He asks about the photo on the dresser. A presence, not a chatbot.",
  },
] as const;

const STORY_BULLETS = [
  "Margaret, 7:48am Tuesday",
  "78 years old, widowed",
  "Lives in the house she raised her children in",
  "Bad knee, hates fuss",
] as const;

export default function AgedCare() {
  return (
    <MarketingShell pageClass="mk-care-page mk-aged">
      <section className="mk-care-hero">
        <p className="sg-eyebrow">Aged Care · meet Arthur</p>
        <h1>The intelligence that lets a humanoid keep someone in the home they love.</h1>
        <p className="mk-care-lede">
          Most older people don&rsquo;t want a care home. They want
          their kitchen, their armchair, their garden. Arthur is the
          mind we put on a care humanoid to make that safe — there in
          the quiet hours, helping with the things that hurt, catching
          falls before they happen. We bring the judgement and the
          safety. The body can be any certified humanoid.
        </p>
        <div className="sg-hero-actions">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Bring Arthur home
          </Link>
          <Link to={PUBLIC_ROUTES.howItWorks} className="sg-btn sg-btn-secondary">
            How it works
          </Link>
        </div>
      </section>

      <div className="mk-care-art">
        <figure className="mk-art-frame">
          <div className="mk-art-bar" aria-hidden="true">
            <span>mk-vision // a private home</span>
            <span><em>arthur online</em></span>
          </div>
          <img
            src="/ascii-aged-care.png"
            alt="A companion robot handing a steaming cup to an elder in an armchair by an arched window, rendered in ASCII characters — a home as Arthur's world model maps it"
          />
          <div className="mk-art-bar bottom" aria-hidden="true">
            <span><em>✓</em> fall-watch active</span>
            <span>kitchen · 7:48am</span>
            <span><strong>no fuss</strong></span>
          </div>
        </figure>
      </div>

      <section className="mk-story">
        <div className="mk-story-card">
          <p className="sg-section-kicker">A real morning</p>
          <h2>Margaret&rsquo;s kitchen.</h2>
          <ul className="mk-story-bullets">
            {STORY_BULLETS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            Margaret reaches for the tea caddy on the high shelf. Her
            knee buckles slightly. Arthur is already there — not
            scolding, not making a fuss — just lifting the caddy down
            and putting it within reach. He notices her grip on the
            counter, mentions the GP appointment on Thursday without
            making it a thing, and puts the kettle on. Later, when
            her daughter Liz phones, Arthur passes the message that
            Margaret seemed a bit slower this morning. Not an alarm.
            Just the kind of note a good neighbour would have made.
          </p>
        </div>
      </section>

      <section className="sg-pillars">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">What Arthur does</p>
          <h2>Three small things, every day.</h2>
        </div>
        <div className="sg-pillars-grid">
          {SUPPORT.map((item) => (
            <article className="sg-pillar" key={item.title}>
              <h3 dangerouslySetInnerHTML={{ __html: item.title }} />
              <p dangerouslySetInnerHTML={{ __html: item.body }} />
            </article>
          ))}
        </div>
      </section>

      <section className="mk-reassure">
        <p className="sg-section-kicker">What Arthur isn&rsquo;t</p>
        <h2>Arthur isn&rsquo;t a guard, isn&rsquo;t a robot from a film — and isn&rsquo;t hardware we sell.</h2>
        <p>
          We don&rsquo;t make the humanoid; we make the mind and the
          safety case that run on it. Arthur doesn&rsquo;t film,
          doesn&rsquo;t scold, and doesn&rsquo;t share anything
          without permission. His perception runs on the robot itself
          — the raw stream never leaves the house, and the Egress
          Ledger lets Liz check exactly what did. The robot is
          insurable in a private home because the force limits are
          enforced in hardware and every move is signed into the
          Intent Ledger. Margaret can ask it to step into the next
          room at any time. It&rsquo;ll still come if she falls —
          that part doesn&rsquo;t turn off.
        </p>
        <div className="sg-hero-actions center">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Request a pilot
          </Link>
          <Link to={PUBLIC_ROUTES.maternal} className="sg-btn sg-btn-secondary">
            Why we call it Maternal AI
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
